import { payrollServiceApi } from "../../app/payrollServiceApi";
import { API_URL } from "../../constants/apiUrl";
import {
  GetIdsResponseType,
  GetQueryArgsType,
  PatchQueryArgsType,
} from "../../types";
import {
  GetAdditionDeductionSalaryParamsType,
  GetAdditionDeductionSalaryResponseType,
  GetDownloadBkashDisbursementResponseType,
  GetHolidayAllowanceParamsType,
  GetHolidayAllowanceResponseType,
  GetMonthlyPayCycleParamsType,
  GetMonthlyPayCycleType,
  GetOvertimeSalaryParamsType,
  GetOvertimeSalaryResponseType,
  GetOvertimeType,
  GetPayComponentParamsType,
  GetTaxData,
  GetVariableSalaryParamsType,
  GetVariableSalaryResponseType,
  PayrollComponentResponseType,
  PayrollSettingResponseType,
} from "../../types/payroll";

export const payrollSettingApi = payrollServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getPayrollSetting: builder.query<PayrollSettingResponseType, void>({
      query: () => ({
        url: API_URL.getPayrollSetting,
      }),
      providesTags: ["GetPayrollSetting"],
    }),
    getMonthlyPayCycle: builder.query<
      GetMonthlyPayCycleType,
      GetQueryArgsType<GetMonthlyPayCycleParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getMonthlyPayCycle,
        ...queryArgs,
      }),
      providesTags: ["GetMonthlyPayCycle"],
    }),
    updateGrossComponent: builder.mutation<unknown, PatchQueryArgsType>({
      query: ({ id, body }) => ({
        url: `${API_URL.updateGrossComponent}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            "GetPayrollSetting",
            "GetMonthlyPayCycle",
            "GetPayComponentSetting",
          ];

        return [];
      },
    }),
    uploadGrossSalary: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadGrossSalary,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetEmployeeBreakdownSalaryDetails"];

        return [];
      },
    }),
    updatePayrollEnableAndPaySchedule: builder.mutation({
      query: ({ businessId, data }) => ({
        url: `${API_URL.updatePayrollEnableAndPaySchedule}/${businessId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetPayrollSetting", "GetPayComponentSetting"];

        return [];
      },
    }),
    updatePayComponent: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updatePayComponent}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetPayrollSetting", "GetPayComponentSetting"];

        return [];
      },
    }),
    getTaxData: builder.query<GetTaxData, void>({
      query: () => ({
        url: API_URL.getTaxData,
      }),
      providesTags: ["GetTaxData"],
    }),
    createOrUpdateTax: builder.mutation({
      query: (data) => ({
        url: API_URL.createOrUpdateTax,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetTaxData"];

        return [];
      },
    }),
    getDownloadBkashDisbursement: builder.query<
      GetDownloadBkashDisbursementResponseType,
      string | number
    >({
      query: (id) => ({
        url: `${API_URL.getDownloadBkashDisbursement}/${id}`,
      }),
    }),
    getOvertime: builder.query<GetOvertimeType, void>({
      query: () => ({
        url: API_URL.getOvertime,
      }),
      providesTags: ["GetOvertime"],
    }),
    createOvertime: builder.mutation({
      query: (data) => ({
        url: API_URL.createOvertime,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetOvertime"];

        return [];
      },
    }),
    getHolidayAllowance: builder.query<GetOvertimeType, void>({
      query: () => ({
        url: API_URL.getHolidayAllowance,
      }),
      providesTags: ["GetHolidayAllowanceSetting"],
    }),
    createHolidayAllowance: builder.mutation({
      query: (data) => ({
        url: API_URL.createHolidayAllowance,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetHolidayAllowanceSetting"];

        return [];
      },
    }),
    updateOvertimeStatus: builder.mutation({
      query: (data) => ({
        url: API_URL.updateOvertimeStatus,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return ["GetPayrollSetting", "GetPayComponentSetting", "GetTaxData"];

        return [];
      },
    }),
    getPayComponentSetting: builder.query<
      PayrollComponentResponseType,
      GetQueryArgsType<GetPayComponentParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getPayComponentSetting,
        ...queryArgs,
      }),
      providesTags: ["GetPayComponentSetting"],
    }),
    getVariableSalary: builder.query<
      GetVariableSalaryResponseType,
      GetQueryArgsType<GetVariableSalaryParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getVariableSalary,
        ...queryArgs,
      }),
      providesTags: ["GetVariableSalary"],
    }),
    uploadVariableSalary: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadVariableSalary,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetVariableSalary", "GetVariableSalaryId"];

        return [];
      },
    }),
    uploadVariableSalaryForEmployee: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadVariableSalaryForEmployee,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetVariableSalary", "GetVariableSalaryId"];

        return [];
      },
    }),
    singleVariableSalaryApprove: builder.mutation<
      void,
      { data: unknown; id: number }
    >({
      query: ({ data, id }) => ({
        url: `${API_URL.singleVariableSalaryApprove}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetVariableSalary", "GetVariableSalaryId"];

        return [];
      },
    }),
    bulkVariableSalaryApprove: builder.mutation({
      query: (data) => ({
        url: API_URL.bulkVariableSalaryApprove,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetVariableSalary", "GetVariableSalaryId"];

        return [];
      },
    }),
    bulkVariableSalaryDelete: builder.mutation({
      query: (data) => ({
        url: API_URL.bulkVariableSalaryDelete,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetVariableSalary", "GetVariableSalaryId"];

        return [];
      },
    }),
    getAdditionDeductionSalary: builder.query<
      GetAdditionDeductionSalaryResponseType,
      GetQueryArgsType<GetAdditionDeductionSalaryParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAdditionDeductionSalary,
        ...queryArgs,
      }),
      providesTags: ["GetAdditionDeductionSalary"],
    }),
    uploadAdditionDeductionSalary: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadAdditionDeductionSalary,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return ["GetAdditionDeductionSalary", "GetAdditionDeductionId"];

        return [];
      },
    }),
    singleAdditionDeductionSalaryApprove: builder.mutation<
      void,
      { data: unknown; id: number }
    >({
      query: ({ data, id }) => ({
        url: `${API_URL.singleAdditionDeductionSalaryApprove}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return ["GetAdditionDeductionSalary", "GetAdditionDeductionId"];

        return [];
      },
    }),
    bulkAdditionDeductionSalaryApprove: builder.mutation({
      query: (data) => ({
        url: API_URL.bulkAdditionDeductionSalaryApprove,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return ["GetAdditionDeductionSalary", "GetAdditionDeductionId"];

        return [];
      },
    }),

    getOvertimeSalary: builder.query<
      GetOvertimeSalaryResponseType,
      GetQueryArgsType<GetOvertimeSalaryParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getOvertimeSalary,
        ...queryArgs,
      }),
      providesTags: ["GetOvertimeSalary"],
    }),
    uploadOvertimeSalary: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadOvertimeSalary,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetOvertimeSalary", "GetOvertimeExcelId"];

        return [];
      },
    }),
    singleOvertimeSalaryApprove: builder.mutation<
      void,
      { data: unknown; id: number }
    >({
      query: ({ data, id }) => ({
        url: `${API_URL.singleOvertimeSalaryApprove}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetOvertimeSalary", "GetOvertimeExcelId"];

        return [];
      },
    }),
    bulkOvertimeSalaryApprove: builder.mutation({
      query: (data) => ({
        url: API_URL.bulkOvertimeSalaryApprove,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetOvertimeSalary", "GetOvertimeExcelId"];

        return [];
      },
    }),

    getHolidayAllowanceSalary: builder.query<
      GetHolidayAllowanceResponseType,
      GetQueryArgsType<GetHolidayAllowanceParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getHolidayAllowanceSalary,
        ...queryArgs,
      }),
      providesTags: ["GetHolidayAllowance"],
    }),
    uploadHolidayAllowanceSalary: builder.mutation({
      query: (data) => ({
        url: API_URL.uploadHolidayAllowanceSalary,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetHolidayAllowance", "GetHolidayAllowanceId"];

        return [];
      },
    }),
    singleHolidayAllowanceSalaryApprove: builder.mutation<
      void,
      { data: unknown; id: number }
    >({
      query: ({ data, id }) => ({
        url: `${API_URL.singleHolidayAllowanceSalaryApprove}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetHolidayAllowance", "GetHolidayAllowanceId"];

        return [];
      },
    }),
    bulkHolidayAllowanceSalaryApprove: builder.mutation({
      query: (data) => ({
        url: API_URL.bulkHolidayAllowanceSalaryApprove,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetHolidayAllowance", "GetHolidayAllowanceId"];

        return [];
      },
    }),

    getVariableSalaryId: builder.query<
      GetIdsResponseType<"variableSalariesIds">,
      GetQueryArgsType<GetVariableSalaryParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getVariableSalaryId,
        ...queryArgs,
      }),
      providesTags: ["GetVariableSalaryId"],
    }),

    getAdditionDeductionId: builder.query<
      GetIdsResponseType<"additionDeductionSalariesIds">,
      GetQueryArgsType<GetAdditionDeductionSalaryParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAdditionDeductionId,
        ...queryArgs,
      }),
      providesTags: ["GetAdditionDeductionId"],
    }),

    getOvertimeExcelId: builder.query<
      GetIdsResponseType<"overtimeExcelIds">,
      GetQueryArgsType<GetOvertimeSalaryParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getOvertimeExcelId,
        ...queryArgs,
      }),
      providesTags: ["GetOvertimeExcelId"],
    }),

    getHolidayAllowanceId: builder.query<
      GetIdsResponseType<"holidayAllowanceExcelIds">,
      GetQueryArgsType<GetHolidayAllowanceParamsType>
    >({
      query: (queryArgs) => ({
        url: API_URL.getHolidayAllowanceId,
        ...queryArgs,
      }),
      providesTags: ["GetHolidayAllowanceId"],
    }),
  }),
});

export const {
  useGetPayrollSettingQuery,
  useGetMonthlyPayCycleQuery,
  useUpdateGrossComponentMutation,
  useUploadGrossSalaryMutation,
  useUpdatePayrollEnableAndPayScheduleMutation,
  useUpdatePayComponentMutation,
  useGetTaxDataQuery,
  useCreateOrUpdateTaxMutation,
  useGetDownloadBkashDisbursementQuery,
  useGetOvertimeQuery,
  useCreateOvertimeMutation,
  useGetHolidayAllowanceQuery,
  useCreateHolidayAllowanceMutation,
  useUpdateOvertimeStatusMutation,
  useGetPayComponentSettingQuery,
  useUploadVariableSalaryMutation,
  useUploadVariableSalaryForEmployeeMutation,
  useGetVariableSalaryQuery,
  useSingleVariableSalaryApproveMutation,
  useBulkVariableSalaryApproveMutation,
  useBulkAdditionDeductionSalaryApproveMutation,
  useGetAdditionDeductionSalaryQuery,
  useSingleAdditionDeductionSalaryApproveMutation,
  useUploadAdditionDeductionSalaryMutation,
  useGetOvertimeSalaryQuery,
  useSingleOvertimeSalaryApproveMutation,
  useUploadOvertimeSalaryMutation,
  useBulkOvertimeSalaryApproveMutation,
  useBulkHolidayAllowanceSalaryApproveMutation,
  useGetHolidayAllowanceSalaryQuery,
  useSingleHolidayAllowanceSalaryApproveMutation,
  useUploadHolidayAllowanceSalaryMutation,
  useGetVariableSalaryIdQuery,
  useGetAdditionDeductionIdQuery,
  useGetOvertimeExcelIdQuery,
  useGetHolidayAllowanceIdQuery,
  useBulkVariableSalaryDeleteMutation,
} = payrollSettingApi;
